import type { LotLineageProjectionRow } from '../types/lot'

/**
 * tier-a-move-transfer-location-addendum.md addendum: the biomass/cost
 * continuity computations (`computeCurrentBiomassPerLot`, `feedWeeklyBiomass`)
 * already correctly walk a lot's rows across every `(farmId,
 * productionUnitId)` bucket it appears in -- but only if those rows are in
 * the input at all. Feed.tsx and Biomass.tsx load production rows and Tier A
 * logs scoped to a single `(farmId, productionUnitId)` context
 * (`listProductionUnitTableRows`, `mortalityLogApi.list`, etc.), so a lot
 * transferred to a new farm/production unit never has its pre-transfer
 * history fetched at all when viewed from its new location -- not mismatched,
 * simply never queried.
 *
 * This module finds which *other* locations a lot has occupied (from its
 * own `self`-relation `LotLineageProjection` rows) so a caller can fetch a
 * small, targeted supplement from just those locations, rather than loading
 * the tenant's full history.
 */

export type LotLocationContext = {
  farmId: string
  productionUnitId: string
}

const locationKey = (location: LotLocationContext): string => `${location.farmId}::${location.productionUnitId}`

/**
 * Distinct prior/other `(farmId, productionUnitId)` pairs a lot's own
 * lineage rows show, excluding the location currently being viewed.
 * Deliberately `self`-relation only -- ancestor/descendant fold-in for
 * merge/split identity is a separate, already-handled concern
 * (`buildFeedCanonicalLotMaps` and friends); this is specifically about the
 * *same* lot's own movement across farms.
 */
export const findOtherSelfLocations = (
  lineageRows: readonly LotLineageProjectionRow[],
  currentContext: LotLocationContext
): LotLocationContext[] => {
  const seen = new Set<string>([locationKey(currentContext)])
  const others: LotLocationContext[] = []

  for (const row of lineageRows) {
    if (row.relation !== 'self') continue
    const farmId = row.farmId?.trim()
    const productionUnitId = row.productionUnitId?.trim()
    if (!farmId || !productionUnitId) continue

    const location = { farmId, productionUnitId }
    const key = locationKey(location)
    if (seen.has(key)) continue
    seen.add(key)
    others.push(location)
  }

  return others
}
